import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import VendorlyLogo from "@/components/VendorlyLogo";
import { ArrowRight, CaretDown, MapPin } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

const TYPES = [
  { v: "doctor", l: "Doctor / Clinic" },
  { v: "grocery", l: "Grocery" },
  { v: "salon", l: "Salon" },
  { v: "restaurant", l: "Restaurant / Café" },
  { v: "retail", l: "Retail store" },
  { v: "other", l: "Other" },
];

export default function Register() {
  const { regionSlug: urlRegionSlug } = useParams();
  const { register } = useAuth();
  const nav = useNavigate();
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState(null);
  const [regionErr, setRegionErr] = useState(null);
  const [f, setF] = useState({
    name: "",
    business_name: "",
    business_type: "retail",
    phone: "",
    email: "",
    password: "",
    state: "",
    city: "",
  });
  const [busy, setBusy] = useState(false);

  // URL param decides whether the region is locked (from a shared link like
  // /register/pune) or user-picked (bare /register with a dropdown).
  const regionIsLocked = Boolean(urlRegionSlug);

  useEffect(() => {
    // Always load the list — needed for the picker when there's no URL region.
    api.get("/public/regions").then(r => setRegions(r.data || [])).catch(() => setRegions([]));
  }, []);

  useEffect(() => {
    const slug = (urlRegionSlug || "gorakhpur").toLowerCase();
    api.get(`/public/regions/${slug}`)
      .then(r => {
        setRegion(r.data);
        setF(prev => ({ ...prev, state: r.data.state, city: r.data.cities?.[0] || "" }));
      })
      .catch(() => {
        if (regionIsLocked) {
          setRegionErr(`Region "${slug}" isn't set up yet.`);
        }
      });
  }, [urlRegionSlug, regionIsLocked]);

  const onPickRegion = (slug) => {
    // For the picker path: swap the region without navigating (keeps the form data)
    const picked = regions.find(r => r.slug === slug);
    if (!picked) return;
    setRegion(picked);
    setF(prev => ({ ...prev, state: picked.state, city: picked.cities?.[0] || "" }));
  };

  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const isValidPhone = (raw) => {
    const digits = (raw || "").replace(/\D/g, "");
    return digits.length >= 10 && digits.length <= 13;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!region) { toast.error("Please pick your city first"); return; }
    if (!isValidPhone(f.phone)) {
      toast.error("Enter a valid phone number (10 digits or +91…)");
      return;
    }
    setBusy(true);
    try {
      await register({ ...f, region_slug: region.slug });
      toast.success("Account created! Redirecting…");
      nav("/app/billing");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Signup failed");
    } finally { setBusy(false); }
  };

  if (regionErr) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center p-6">
        <div className="bg-white border border-brand-border rounded-3xl p-10 text-center max-w-md">
          <h1 className="font-heading text-2xl font-bold">Region not available</h1>
          <p className="mt-2 text-brand-text/70">{regionErr}</p>
          <Link to="/register" className="mt-6 inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full px-5 py-2.5 text-sm font-semibold">
            Pick your city <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg text-brand-text flex flex-col dot-bg" data-testid="register-page">
      <Toaster position="top-right" richColors />
      <div className="max-w-7xl w-full mx-auto px-6 lg:px-10 py-6">
        <Link to="/" className="inline-flex items-center gap-2">
          <VendorlyLogo size={30} />
          <span className="font-heading font-bold text-xl tracking-tight">Vendorly</span>
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-lg bg-white border border-brand-border rounded-3xl p-8 sm:p-10 shadow-[0_10px_40px_rgba(44,42,41,0.06)]">
          {regionIsLocked && region ? (
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-brand-primary bg-brand-primary/10 rounded-full px-3 py-1 mb-4" data-testid="region-badge">
              <MapPin size={12} weight="duotone" /> {region.name}, {region.state}
            </div>
          ) : (
            <div className="mb-5" data-testid="region-picker-wrap">
              <label className="text-xs uppercase tracking-[0.2em] text-brand-secondary">Your city</label>
              <div className="relative mt-1.5">
                <select
                  value={region?.slug || ""}
                  onChange={(e) => onPickRegion(e.target.value)}
                  data-testid="region-picker"
                  className="w-full h-12 pl-4 pr-10 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none appearance-none"
                >
                  <option value="" disabled>Select your city…</option>
                  {regions.map(r => (
                    <option key={r.slug} value={r.slug}>{r.name} — {r.state}</option>
                  ))}
                </select>
                <CaretDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-textMuted pointer-events-none" />
              </div>
              <p className="mt-1 text-[11px] text-brand-textMuted">
                Vendorly is currently live in {regions.length} {regions.length === 1 ? "city" : "cities"}. More coming soon.
              </p>
            </div>
          )}
          <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-2">Get started</div>
          <h1 className="font-heading font-bold text-3xl tracking-tight">Create your vendor account.</h1>
          <p className="text-brand-text/70 mt-2">Takes under a minute.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Your name" value={f.name} onChange={on("name")} required testid="reg-name" placeholder="Dikshant" />
              <Field label="Business name" value={f.business_name} onChange={on("business_name")} required testid="reg-business" placeholder="Dikshant's Shop" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold">Business type</label>
                <div className="relative mt-1.5">
                  <select value={f.business_type} onChange={on("business_type")} data-testid="reg-type"
                    className="w-full h-12 pl-4 pr-10 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none appearance-none">
                    {TYPES.map(t=><option key={t.v} value={t.v}>{t.l}</option>)}
                  </select>
                  <CaretDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-textMuted pointer-events-none" />
                </div>
              </div>
              <Field label="Phone" value={f.phone} onChange={on("phone")} required testid="reg-phone" placeholder="+91 98765 43210" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold">State</label>
                <input value={region?.state || ""} disabled data-testid="reg-state"
                  className="mt-1.5 w-full h-12 px-4 rounded-xl bg-brand-bg border border-brand-border text-brand-textMuted cursor-not-allowed" />
              </div>
              <div>
                <label className="text-sm font-semibold">City</label>
                <div className="relative mt-1.5">
                  <select value={f.city} onChange={on("city")} data-testid="reg-city" disabled={!region}
                    className="w-full h-12 pl-4 pr-10 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none appearance-none disabled:opacity-70 disabled:cursor-not-allowed">
                    {(region?.cities || []).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <CaretDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-textMuted pointer-events-none" />
                </div>
              </div>
            </div>
            <Field label="Email" type="email" value={f.email} onChange={on("email")} required testid="reg-email" placeholder="you@shop.com" />
            <Field label="Password" type="password" value={f.password} onChange={on("password")} required testid="reg-password" placeholder="Min 6 characters" />
            <button type="submit" disabled={busy || !region}
              className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold transition-colors disabled:opacity-60"
              data-testid="reg-submit">
              {busy ? "Creating…" : (<>Create account <ArrowRight size={18} weight="bold" /></>)}
            </button>
          </form>
          <div className="mt-6 text-sm text-brand-text/70">
            Already have an account? <Link to="/login" className="text-brand-primary font-semibold hover:underline" data-testid="reg-to-login">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="text-sm font-semibold">{label}</label>
      <input {...rest} data-testid={testid}
        className="mt-1.5 w-full h-12 px-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none transition-colors" />
    </div>
  );
}
